import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, Check, CheckCheck, Inbox, UserCheck, GitMerge, TrendingUp, Zap, AlertTriangle, MessageCircle, AlarmClock } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import {
  notificationsService,
  AppNotification,
  NotificationType,
} from '@/services/notifications.service';

// LocalStorage keys for mute toggles.
const MUTE_KEY = 'slighter.notifications.muted';
const MUTED_LEADS_KEY = 'slighter.notifications.muted-leads';

function isMuted(): boolean {
  try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
}

function setMuted(v: boolean) {
  try {
    if (v) localStorage.setItem(MUTE_KEY, '1');
    else localStorage.removeItem(MUTE_KEY);
  } catch { /* ignore quota */ }
}

/** Per-conversation mute lookup. Cheap — read once per SSE event. */
function isLeadMuted(leadUid?: string): boolean {
  if (!leadUid) return false;
  try {
    const raw = localStorage.getItem(MUTED_LEADS_KEY);
    if (!raw) return false;
    const arr = JSON.parse(raw) as string[];
    return Array.isArray(arr) && arr.includes(leadUid);
  } catch { return false; }
}

/** Plays a short notification chime via WebAudio. Best-effort — silent if blocked. */
function playChime() {
  try {
    const Ctx = (window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.18);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.25);
    osc.onended = () => ctx.close().catch(() => undefined);
  } catch { /* never throw from here */ }
}

/** Best-effort browser Notification popup — falls back silently if denied. */
function notifyOS(title: string, body?: string) {
  try {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'granted') {
      new Notification(title, { body, tag: 'slighter-crm', icon: '/favicon.ico' });
    } else if (Notification.permission === 'default') {
      Notification.requestPermission().then((p) => {
        if (p === 'granted') new Notification(title, { body, tag: 'slighter-crm' });
      }).catch(() => undefined);
    }
  } catch { /* ignore */ }
}

// ─── Icon per notification type ──────────────────────────────────────────────

function TypeIcon({ type }: { type: NotificationType }) {
  const cls = 'shrink-0';
  switch (type) {
    case 'lead_assigned':      return <UserCheck size={14} className={clsx(cls, 'text-primary')} />;
    case 'lead_handed_off':    return <GitMerge size={14} className={clsx(cls, 'text-warning')} />;
    case 'lead_status_changed': return <TrendingUp size={14} className={clsx(cls, 'text-success')} />;
    case 'lead_followup_due':  return <AlarmClock size={14} className={clsx(cls, 'text-danger')} />;
    case 'campaign_completed': return <Zap size={14} className={clsx(cls, 'text-info')} />;
    case 'template_approved':  return <Check size={14} className={clsx(cls, 'text-success')} />;
    case 'template_rejected':  return <AlertTriangle size={14} className={clsx(cls, 'text-danger')} />;
    case 'ai_max_turns_exceeded': return <AlertTriangle size={14} className={clsx(cls, 'text-warning')} />;
    case 'whatsapp_inbound':   return <MessageCircle size={14} className={clsx(cls, 'text-success')} />;
    default:                   return <Bell size={14} className={clsx(cls, 'text-text-muted')} />;
  }
}

// ─── Individual row ───────────────────────────────────────────────────────────

function NotifRow({
  notif,
  onRead,
  onNavigate,
}: {
  notif: AppNotification;
  onRead: (uid: string) => void;
  onNavigate: (notif: AppNotification) => void;
}) {
  const isUnread = !notif.readAt;
  const leadUid = notif.data?.leadUid as string | undefined;
  const isLeadNotif = notif.type.startsWith('lead_') || notif.type === 'whatsapp_inbound';

  const handleClick = () => {
    if (isUnread) onRead(notif.notificationUid);
    if (isLeadNotif && leadUid) onNavigate(notif);
  };

  return (
    <button
      onClick={handleClick}
      className={clsx(
        'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
        'hover:bg-surface-alt/70',
        isUnread && 'bg-primary-soft/20',
      )}
    >
      {/* Type icon */}
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-alt border border-border">
        <TypeIcon type={notif.type} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className={clsx('text-[12.5px] leading-snug truncate', isUnread ? 'font-semibold text-text-primary' : 'text-text-secondary')}>
            {notif.title}
          </p>
          {isUnread && (
            <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Unread" />
          )}
        </div>
        {notif.body && (
          <p className="mt-0.5 text-[11.5px] text-text-muted leading-snug line-clamp-2">{notif.body}</p>
        )}
        <p className="mt-1 text-[10.5px] text-text-muted">
          {formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true })}
        </p>
      </div>
    </button>
  );
}

// ─── Bell component ───────────────────────────────────────────────────────────

export const NotificationsBell: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [bouncing, setBouncing] = useState(false);
  const [muted, setMutedState] = useState<boolean>(isMuted());
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const esRef = useRef<EventSource | null>(null);
  const mutedRef = useRef<boolean>(muted);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  const toggleMute = () => {
    const next = !muted;
    setMutedState(next);
    setMuted(next);
    if (!next) {
      // Asking permission feels right at the moment they un-mute.
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => undefined);
      }
    }
  };

  // ── Load initial data ───────────────────────────────────────────────────
  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, countRes] = await Promise.all([
        notificationsService.list({ page: 1, limit: 50 }),
        notificationsService.unreadCount(),
      ]);
      setNotifications(listRes.items);
      setUnread(countRes.count);
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // ── SSE stream ───────────────────────────────────────────────────────────
  useEffect(() => {
    let es: EventSource | null = null;
    try {
      es = notificationsService.openStream();
      esRef.current = es;
      if (!es) return;

      es.onmessage = (event: MessageEvent) => {
        try {
          const notif: AppNotification = JSON.parse(event.data as string);
          setNotifications((prev) => {
            // Avoid duplicates
            if (prev.some((n) => n.notificationUid === notif.notificationUid)) return prev;
            return [notif, ...prev];
          });
          setUnread((n) => n + 1);
          // Bell bounce animation
          setBouncing(true);
          setTimeout(() => setBouncing(false), 800);

          // Show toast for the incoming notification (always — visual cue
          // without sound when muted is OK).
          toast(notif.title, {
            icon: notif.type === 'whatsapp_inbound' ? '💬' : '🔔',
            duration: 4000,
          });

          // Sound + OS popup are gated by global mute AND per-lead mute.
          const leadUid = (notif.data as Record<string, unknown> | undefined)?.leadUid as string | undefined;
          const perLeadMuted = notif.type === 'whatsapp_inbound' && isLeadMuted(leadUid);
          if (!mutedRef.current && !perLeadMuted) {
            playChime();
            notifyOS(notif.title, notif.body);
          }
        } catch {
          // ignore malformed SSE data
        }
      };

      es.onerror = () => {
        // EventSource auto-reconnects; just log silently
      };
    } catch {
      // SSE not available (e.g. in test env)
    }

    return () => {
      es?.close();
    };
  }, []);

  // ── Click outside to close ───────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        !buttonRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleRead = async (uid: string) => {
    try {
      await notificationsService.markAsRead(uid);
      setNotifications((prev) =>
        prev.map((n) => n.notificationUid === uid ? { ...n, readAt: new Date().toISOString() } : n),
      );
      setUnread((n) => Math.max(0, n - 1));
    } catch {
      // non-critical
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() })));
      setUnread(0);
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Failed to mark notifications as read');
    }
  };

  const handleNavigate = (notif: AppNotification) => {
    const leadUid = notif.data?.leadUid as string | undefined;
    if (leadUid) {
      navigate(`/leads/${leadUid}`);
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-9 w-9 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
        aria-label="Notifications"
        aria-expanded={open}
      >
        <Bell size={15} className={bouncing ? 'animate-bell-bounce' : ''} />
        {unread > 0 && (
          <span className="absolute right-1.5 top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-white leading-none">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          className={clsx(
            'absolute right-0 top-full mt-2 z-50',
            'w-80 rounded-2xl border border-border bg-surface shadow-elevated overflow-hidden',
          )}
          style={{ maxHeight: 480 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="font-display text-sm font-semibold text-text-primary">Notifications</h3>
            <div className="flex items-center gap-3">
              <button
                onClick={toggleMute}
                title={muted ? 'Sounds muted — click to unmute' : 'Mute sounds and OS popups'}
                className={clsx(
                  'flex items-center gap-1 rounded p-1 transition-colors',
                  muted ? 'text-text-muted hover:text-text-primary' : 'text-primary hover:text-primary/80',
                )}
              >
                {muted ? <BellOff size={13} /> : <Bell size={13} />}
              </button>
              {unread > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="flex items-center gap-1 text-[11.5px] font-semibold text-primary hover:underline"
                >
                  <CheckCheck size={12} />
                  Mark all read
                </button>
              )}
            </div>
          </div>
          {muted && (
            <div className="border-b border-border bg-warning-soft/40 px-4 py-2 text-[11px] text-warning">
              Sounds + OS popups muted. You'll still see toasts and the badge counter.
            </div>
          )}

          {/* List */}
          <div className="overflow-y-auto" style={{ maxHeight: 400 }}>
            {loading ? (
              <div className="divide-y divide-border">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex items-start gap-3 px-4 py-3">
                    <div className="skeleton h-7 w-7 rounded-full shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="skeleton h-3.5 w-36 rounded" />
                      <div className="skeleton h-3 w-28 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-alt text-text-muted">
                  <Inbox size={18} />
                </div>
                <p className="text-xs text-text-muted">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications.map((n) => (
                  <NotifRow
                    key={n.notificationUid}
                    notif={n}
                    onRead={handleRead}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
